import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthApiService, CampaignApiService } from '@digilize/shared/data/services';
import { SnackbarService } from '@digilize/shared/feature/modules/snackbar/src';
import { DialogService } from '@digilize/shared/utils/services/src';
import { getJwtExpiration } from '@digilize/shared/utils/helpers';
import { APP_CONFIG, WINDOW } from '@digilize/shared/utils/tokens';
import { AppConfig, PossibleApiPrefix, Token } from '@shared/definitions';
import { CookieService } from 'ngx-cookie-service';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  DialogLostSessionComponent,
  DialogLostSessionComponentData,
} from './dialog-lost-session/dialog-lost-session.component';

const MS_PER_MINUTE = 60 * 1000;

/**
 * Epoch milliseconds `minutes` from now — the only thing this file ever asked moment for, alongside
 * `moment.now()`. Both are plain arithmetic, and importing moment for them put 141 kB in the initial
 * bundle of every app that authenticates.
 */
const minutesFromNow = (minutes: number): number => Date.now() + minutes * MS_PER_MINUTE;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly REFRESH_TOKEN_EXPIRATION = 60 * 24 * 365 * 100; // In minutes (100years)
  private USER_INTERACTED_WITH_APP = false;

  constructor(
    @Inject(APP_CONFIG) private appConfig: AppConfig,
    private authApiService: AuthApiService,
    private cookieService: CookieService,
    private dialogService: DialogService,
    private router: Router,
    private campaignApiService: CampaignApiService,
    private snackbarService: SnackbarService,
    @Inject(WINDOW) private window: Window
  ) {
    this.window.addEventListener('click', () => {
      this.USER_INTERACTED_WITH_APP = true;
    });
  }

  isLoggedIn(): boolean {
    return !!this.getRefreshToken();
  }

  getAccessToken(apiPrefix: PossibleApiPrefix = 'api'): string | null {
    const token = localStorage.getItem(this.patchWithAppLocalAuthPrefix('token', apiPrefix));
    const tokenExpiration = localStorage.getItem(this.patchWithAppLocalAuthPrefix('tokenExpiration', apiPrefix));
    if (Number(tokenExpiration) > Date.now()) {
      return token;
    }
    return null;
  }

  getRefreshToken(apiPrefix: PossibleApiPrefix = 'api'): string | null {
    const token = localStorage.getItem(this.patchWithAppLocalAuthPrefix('refresh_token', apiPrefix));
    const tokenExpiration = localStorage.getItem(this.patchWithAppLocalAuthPrefix('refreshTokenExpiration', apiPrefix));

    if (Number(tokenExpiration) > Date.now()) {
      return token;
    }

    return null;
  }

  logout(logoutRoute: string | null = '/'): void {
    this.removeSession();

    if (logoutRoute) {
      this.window.location.href = logoutRoute;
    }
  }

  softLogout(logoutRoute: string | null = '/'): void {
    this.removeSession();

    if (logoutRoute) {
      this.router.navigate([logoutRoute]);
    }
  }

  refreshAccessToken(apiPrefix: PossibleApiPrefix = 'api'): Observable<null | Token> {
    const { token, refreshExpiration } = this.getToken(apiPrefix);

    if (refreshExpiration && Number(refreshExpiration) > Date.now()) {
      return this.authApiService.refreshToken(token, apiPrefix).pipe(
        tap({
          next: (newToken: Token) => {
            this.setSession(newToken, null, apiPrefix);
          },
          error: (error) => {
            /* A 429 is weather, not a verdict: the allowance is spent, nobody has said the session is
               bad, and clearing it here would sign the user out over a busy minute. */
            if (error?.status === 429) {
              this.snackbarService.openSnackbarWithApiError(error.error.error, 20000);
              return;
            }

            this.endSessionAfterFailedRenewal();
          },
        })
      );
    }

    return of(null);
  }

  /**
   * Routes to login after a renewal the server actually refused.
   *
   * Both branches end in the same place; the dialog is only there to explain the jump to somebody who
   * was mid-task. `USER_INTERACTED_WITH_APP` resets on every page load, so a renewal that fails before
   * the first click takes the quiet branch.
   *
   * The `window.location.reload()` that used to follow `logout()` here is gone, and it is half of what
   * PAUL-3246 looked like from a user's seat: the page appearing to crash and come back, with whatever
   * was on screen lost. It could not have helped either — `logout()` is already navigating away, and a
   * reload of the page being left just races that navigation. Everything else is deliberately unchanged,
   * because this method runs in all thirteen apps that register the shared interceptor.
   */
  private endSessionAfterFailedRenewal(): void {
    const isAdminPanel = this.window.location.href.includes('/admin');

    if (isAdminPanel && this.USER_INTERACTED_WITH_APP) {
      const dialog = this.dialogService.openDialog(DialogLostSessionComponent);
      dialog.afterClosed().subscribe(() => this.softLogout('/auth'));

      return;
    }

    this.logout();
  }

  refreshAccessTokenManualRetry(apiPrefix: PossibleApiPrefix = 'api'): Observable<Token | null> {
    const { token, refreshExpiration } = this.getToken(apiPrefix);

    if (refreshExpiration && Number(refreshExpiration) > Date.now()) {
      return this.authApiService.refreshToken(token, apiPrefix).pipe(
        tap({
          next: (newToken: Token) => {
            this.setSession(newToken, null, apiPrefix);
          },
          error: (error) => {
            this.dialogService.openDialog<DialogLostSessionComponentData>(DialogLostSessionComponent, {
              data: { closeable: false },
              disableClose: true,
            });
            if (error.status === 429) {
              this.snackbarService.openSnackbarWithApiError(error.error.error, 20000);
              return of(null);
            }
          },
        })
      );
    }

    return of(null);
  }

  setSession(token: Token, callback = null, apiPrefix: PossibleApiPrefix = 'api'): void {
    const refreshTokenExpiration = minutesFromNow(this.REFRESH_TOKEN_EXPIRATION);

    if (token.token) {
      localStorage.setItem(this.patchWithAppLocalAuthPrefix('token', apiPrefix), token.token);
      localStorage.setItem(
        this.patchWithAppLocalAuthPrefix('tokenExpiration', apiPrefix),
        JSON.stringify(this.resolveTokenExpiration(token.token, apiPrefix))
      );
    }

    if (token.refresh_token) {
      localStorage.setItem(this.patchWithAppLocalAuthPrefix('refresh_token', apiPrefix), token.refresh_token);
      localStorage.setItem(
        this.patchWithAppLocalAuthPrefix('refreshTokenExpiration', apiPrefix),
        JSON.stringify(refreshTokenExpiration)
      );
    }
    this.authApiService.getMe();

    // Claim cart session
    if (callback) {
      const claimCallback = (): void => {
        if (localStorage.getItem('checkout_return')) {
          const returnUrl = localStorage.getItem('checkout_return');
          localStorage.removeItem('checkout_return');
          this.window.location.href = returnUrl;
        } else {
          callback();
        }
      };

      const sessionId = this.cookieService.get('session_id');
      if (sessionId) {
        this.authApiService.claimCartSession(sessionId).subscribe({
          next: () => claimCallback(),
          error: () => claimCallback(),
        });
      } else {
        claimCallback();
      }
    }
  }

  setAcquisitionSession(token: Token, redirectUrl: string, returnUrl: string, app: string): void {
    this.setSession(token, () => {
      const acquisitionId = this.cookieService.get('acquisition_id');
      if (acquisitionId) {
        this.registerAcquisition(acquisitionId, redirectUrl, returnUrl, app);
      } else {
        this.window.location.href =
          app === 'dashboard' || this.appConfig.app === 'flixit'
            ? '/admin/auth'
            : decodeURIComponent(redirectUrl) + returnUrl;
      }
    });
  }

  private registerAcquisition(id: string, redirectUrl?: string, returnUrl?: string, app?: string): void {
    this.campaignApiService.registerAcquisition(id).subscribe({
      next: () => {
        this.cookieService.delete('acquisition_id');
        this.window.location.href =
          app === 'dashboard' || this.appConfig.app === 'flixit'
            ? '/admin/c/0/shops'
            : decodeURIComponent(redirectUrl) + returnUrl;
      },
      error: (error) => {
        if (error?.status === 400) {
          this.cookieService.delete('acquisition_id');
        }

        if (error.error?.error?.info?.kind === 'already_exists') {
          this.window.location.href =
            app === 'dashboard' || this.appConfig.app === 'flixit'
              ? '/admin/c/0/shops'
              : decodeURIComponent(redirectUrl) + returnUrl;
        } else {
          this.router.navigate(['niet-gevonden']);
        }
      },
    });
  }

  /**
   * When the stored access token should be treated as due for renewal, as a millisecond epoch.
   *
   * The token's own `exp` where the app asks for it and the token can be read, and the app's configured
   * lifetime otherwise. Nothing here is a constant: an expiry the frontend invented, unrelated to what
   * the server stamped, is what PAUL-3246 was — 60 seconds against a token good for five, renewing four
   * times more often than needed and burning through the account's session slots.
   *
   * The fallback is what keeps the non-Pauly apps working. Several of their backends issue tokens that
   * are not JWTs at all, so `getJwtExpiration` answers `null` and the configured value decides.
   */
  private resolveTokenExpiration(token: string, apiPrefix: PossibleApiPrefix): number {
    const { source, minutes, minutes2 } = this.appConfig.tokenExpiration;
    const configured = apiPrefix === 'api2' ? minutes2 ?? minutes : minutes;

    if (source === 'jwt') {
      const fromToken = getJwtExpiration(token);

      if (fromToken !== null) {
        return fromToken;
      }
    }

    return minutesFromNow(configured);
  }

  private patchWithAppLocalAuthPrefix(localStorageKey: string, apiPrefix: PossibleApiPrefix = 'api'): string {
    /* to distinguish localstorage keys in certain apps (when deployed on same domain apps can share localstorage)  */
    switch (apiPrefix) {
      case 'api':
        const prefix = this.appConfig.localStorageAuthPrefix || '';
        return `${prefix}${localStorageKey}`;
      case 'api2':
        const prefix2 = this.appConfig.localStorageAuthPrefix2 || '';
        return `${prefix2}${localStorageKey}`;
      default:
        throw new Error(`${apiPrefix} is not handled in patchWithAppLocalAuthPrefix`);
    }
  }

  private removeSession(): void {
    localStorage.removeItem(this.patchWithAppLocalAuthPrefix('token'));
    localStorage.removeItem(this.patchWithAppLocalAuthPrefix('tokenExpiration'));
    localStorage.removeItem(this.patchWithAppLocalAuthPrefix('refresh_token'));
    localStorage.removeItem(this.patchWithAppLocalAuthPrefix('refreshTokenExpiration'));
    this.cookieService.delete('session_id', '/');
    this.authApiService.setMe(null);
  }

  private getToken(apiPrefix: PossibleApiPrefix = 'api'): { token: Token; refreshExpiration: string } {
    const token: Token = {
      token: localStorage.getItem(this.patchWithAppLocalAuthPrefix('token', apiPrefix)),
      refresh_token: localStorage.getItem(this.patchWithAppLocalAuthPrefix('refresh_token', apiPrefix)),
    };
    const refreshExpiration = localStorage.getItem(
      this.patchWithAppLocalAuthPrefix('refreshTokenExpiration', apiPrefix)
    );

    return { token, refreshExpiration };
  }
}
